/**
 * MobileContextMenu Component - Compact Dropdown
 * 
 * Enhanced mobile hamburger menu for page-aware navigation and context actions.
 * Replaces simple TopBar.onMenu callback with structured menu configuration.
 * 
 * Design: Compact dropdown positioned relative to hamburger button, not full-screen drawer.
 * 
 * GitHub Issue: #406 - Mobile Context Menu Enhancement
 * GitHub Issue: #448 - TopBar Menu Full-Screen Overlay Fix
 */

import { useRef, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { type LucideIcon, Home, Users, Calendar, User, Settings, Plus, UserMinus, UserPlus, LogOut, Download, Trash2, Info, HelpCircle, FileText, Shield } from 'lucide-react';

// ===============================
// TYPE DEFINITIONS
// ===============================

export interface NavigationItem {
  to: string;
  label: string;
  icon: LucideIcon;
  show?: boolean; // Conditional visibility
}

export interface ContextAction {
  label: string;
  icon: LucideIcon;
  onClick: () => void;
  variant?: 'default' | 'destructive';
  show?: boolean; // Conditional visibility
}

export interface AuthAction {
  label: string;
  onClick: () => void;
  variant?: 'default' | 'destructive';
  icon?: LucideIcon;
  show?: boolean;
}

export interface MobileMenuConfig {
  navigation?: NavigationItem[];
  contextActions?: ContextAction[];
  authentication?: AuthAction[];
}

interface MobileContextMenuProps {
  config: MobileMenuConfig;
  isOpen: boolean;
  onClose: () => void;
  className?: string;
}

// ===============================
// MENU CONFIGURATIONS
// ===============================

// Example configurations for different pages
export const createHomeMobileMenu = (_navigate: ReturnType<typeof useNavigate>, logout: () => void): MobileMenuConfig => ({
  navigation: [
    { to: '/clubs', label: 'Clubs', icon: Users },
    { to: '/events', label: 'Events', icon: Calendar },
    { to: '/me', label: 'Profile', icon: User },
    { to: '/about', label: 'About', icon: Info },
    { to: '/faq', label: 'FAQ', icon: HelpCircle },
    { to: '/terms', label: 'Terms', icon: FileText },
    { to: '/privacy', label: 'Privacy', icon: Shield }
  ],
  authentication: [
    { label: 'Sign Out', onClick: logout, variant: 'destructive', icon: LogOut }
  ]
});

export const createClubsMobileMenu = (navigate: ReturnType<typeof useNavigate>, logout: () => void): MobileMenuConfig => ({
  navigation: [
    { to: '/home', label: 'Home', icon: Home },
    { to: '/events', label: 'Events', icon: Calendar },
    { to: '/me', label: 'Profile', icon: User },
    { to: '/about', label: 'About', icon: Info },
    { to: '/faq', label: 'FAQ', icon: HelpCircle },
    { to: '/terms', label: 'Terms', icon: FileText },
    { to: '/privacy', label: 'Privacy', icon: Shield }
  ],
  contextActions: [
    { 
      label: 'Create Club', 
      onClick: () => navigate('/clubs/create'), 
      icon: Plus 
    }
  ],
  authentication: [
    { label: 'Sign Out', onClick: logout, variant: 'destructive', icon: LogOut }
  ]
});

export const createClubDetailMobileMenu = (
  navigate: ReturnType<typeof useNavigate>, 
  logout: () => void,
  clubSlug: string,
  isClubLeader: boolean,
  userMembership: boolean,
  handleMembershipToggle: () => void
): MobileMenuConfig => ({
  navigation: [
    { to: '/home', label: 'Home', icon: Home },
    { to: '/events', label: 'Events', icon: Calendar },
    { to: '/me', label: 'Profile', icon: User },
    { to: '/about', label: 'About', icon: Info },
    { to: '/faq', label: 'FAQ', icon: HelpCircle },
    { to: '/terms', label: 'Terms', icon: FileText },
    { to: '/privacy', label: 'Privacy', icon: Shield }
  ],
  contextActions: [
    { 
      label: 'Club Settings', 
      onClick: () => navigate(`/clubs/${clubSlug}/settings`), 
      icon: Settings,
      show: isClubLeader // Permission-based visibility
    },
    { 
      label: userMembership ? 'Leave Club' : 'Join Club',
      onClick: handleMembershipToggle,
      icon: userMembership ? UserMinus : UserPlus,
      variant: userMembership ? 'destructive' : 'default'
    }
  ],
  authentication: [
    { label: 'Sign Out', onClick: logout, variant: 'destructive', icon: LogOut }
  ]
});

export const createEventsMobileMenu = (navigate: ReturnType<typeof useNavigate>, logout: () => void): MobileMenuConfig => ({
  navigation: [
    { to: '/home', label: 'Home', icon: Home },
    { to: '/clubs', label: 'Clubs', icon: Users },
    { to: '/me', label: 'Profile', icon: User },
    { to: '/about', label: 'About', icon: Info },
    { to: '/faq', label: 'FAQ', icon: HelpCircle },
    { to: '/terms', label: 'Terms', icon: FileText },
    { to: '/privacy', label: 'Privacy', icon: Shield }
  ],
  contextActions: [
    { 
      label: 'Create Event', 
      onClick: () => navigate('/events/create'), 
      icon: Plus 
    }
  ],
  authentication: [
    { label: 'Sign Out', onClick: logout, variant: 'destructive', icon: LogOut }
  ]
});

export const createProfileMobileMenu = (navigate: ReturnType<typeof useNavigate>, logout: () => void): MobileMenuConfig => ({
  navigation: [
    { to: '/home', label: 'Home', icon: Home },
    { to: '/clubs', label: 'Clubs', icon: Users },
    { to: '/events', label: 'Events', icon: Calendar },
    { to: '/about', label: 'About', icon: Info },
    { to: '/faq', label: 'FAQ', icon: HelpCircle },
    { to: '/terms', label: 'Terms', icon: FileText },
    { to: '/privacy', label: 'Privacy', icon: Shield }
  ],
  contextActions: [
    { label: 'Settings', onClick: () => navigate('/settings'), icon: Settings }
  ],
  authentication: [
    { label: 'Sign Out', onClick: logout, variant: 'destructive', icon: LogOut }
  ]
});

export const createEventDetailMobileMenu = (
  navigate: ReturnType<typeof useNavigate>,
  logout: () => void,
  eventId: string,
  canEdit: boolean,
  onShare?: () => void
): MobileMenuConfig => ({
  navigation: [
    { to: '/home', label: 'Home', icon: Home },
    { to: '/clubs', label: 'Clubs', icon: Users },
    { to: '/events', label: 'Events', icon: Calendar },
    { to: '/about', label: 'About', icon: Info },
    { to: '/faq', label: 'FAQ', icon: HelpCircle },
    { to: '/terms', label: 'Terms', icon: FileText },
    { to: '/privacy', label: 'Privacy', icon: Shield }
  ],
  contextActions: [
    { 
      label: 'Edit Event', 
      onClick: () => navigate(`/event/${eventId}/edit`), 
      icon: Settings,
      show: canEdit // Permission-based visibility
    },
    { 
      label: 'Share Event', 
      onClick: onShare || (() => {
        // Fallback share logic
        if (navigator.share) {
          navigator.share({
            title: 'Check out this event',
            url: window.location.href
          }).catch(() => {});
        }
      }), 
      icon: Plus // Using Plus as Share icon not imported yet
    }
  ],
  authentication: [
    { label: 'Sign Out', onClick: logout, variant: 'destructive', icon: LogOut }
  ]
});

export const createClubSettingsMobileMenu = (
  _navigate: ReturnType<typeof useNavigate>,
  logout: () => void,
  clubSlug: string,
  isOwner: boolean,
  setShowDeleteConfirm: (show: boolean) => void
): MobileMenuConfig => ({
  navigation: [
    { to: '/home', label: 'Home', icon: Home },
    { to: '/clubs', label: 'Clubs', icon: Users },
    { to: '/events', label: 'Events', icon: Calendar },
    { to: `/clubs/${clubSlug}`, label: 'Back to Club', icon: Users },
    { to: '/about', label: 'About', icon: Info },
    { to: '/faq', label: 'FAQ', icon: HelpCircle },
    { to: '/terms', label: 'Terms', icon: FileText },
    { to: '/privacy', label: 'Privacy', icon: Shield }
  ],
  contextActions: [
    { 
      label: 'Delete Club', 
      onClick: () => setShowDeleteConfirm(true),
      icon: Trash2,
      variant: 'destructive' as const,
      show: isOwner // Owner-only action - from useClub(), never calculated locally
    }
  ],
  authentication: [
    { label: 'Sign Out', onClick: logout, variant: 'destructive', icon: LogOut }
  ]
});

/**
 * Club Members Page Menu
 * Leader-only actions: invite members, manage roles
 */
export const createClubMembersMobileMenu = (
  navigate: ReturnType<typeof useNavigate>,
  logout: () => void,
  clubSlug: string,
  isLeader: boolean
): MobileMenuConfig => ({
  navigation: [
    { to: '/home', label: 'Home', icon: Home },
    { to: '/clubs', label: 'Clubs', icon: Users },
    { to: '/events', label: 'Events', icon: Calendar },
    { to: `/clubs/${clubSlug}`, label: 'Back to Club', icon: Users },
    { to: '/about', label: 'About', icon: Info },
    { to: '/faq', label: 'FAQ', icon: HelpCircle },
    { to: '/terms', label: 'Terms', icon: FileText },
    { to: '/privacy', label: 'Privacy', icon: Shield }
  ],
  contextActions: [
    { 
      label: 'Invite Members', 
      onClick: () => navigate(`/clubs/${clubSlug}/invite`), 
      icon: UserPlus,
      show: isLeader // Leader-only action
    },
    { 
      label: 'Manage Roles', 
      onClick: () => navigate(`/clubs/${clubSlug}/settings`), 
      icon: Settings,
      show: isLeader // Leader-only action (settings has role management)
    }
  ],
  authentication: [
    { label: 'Sign Out', onClick: logout, variant: 'destructive', icon: LogOut }
  ]
});

/**
 * User Settings Page Menu
 * Personal account preferences and profile management
 */
export const createSettingsMobileMenu = (
  _navigate: ReturnType<typeof useNavigate>,
  logout: () => void
): MobileMenuConfig => ({
  navigation: [
    { to: '/home', label: 'Home', icon: Home },
    { to: '/clubs', label: 'Clubs', icon: Users },
    { to: '/events', label: 'Events', icon: Calendar },
    { to: '/me', label: 'My Profile', icon: User },
    { to: '/about', label: 'About', icon: Info },
    { to: '/faq', label: 'FAQ', icon: HelpCircle },
    { to: '/terms', label: 'Terms', icon: FileText },
    { to: '/privacy', label: 'Privacy', icon: Shield }
  ],
  contextActions: [
    { 
      label: 'Export Data', 
      onClick: () => alert('Data export feature coming soon'), 
      icon: Download
    }
  ],
  authentication: [
    { label: 'Sign Out', onClick: logout, variant: 'destructive', icon: LogOut }
  ]
});

/**
 * Club Events Page Menu
 * Leader-only: create events
 */
export const createClubEventsMobileMenu = (
  navigate: ReturnType<typeof useNavigate>,
  logout: () => void,
  clubSlug: string,
  isLeader: boolean
): MobileMenuConfig => ({
  navigation: [
    { to: '/home', label: 'Home', icon: Home },
    { to: '/clubs', label: 'Clubs', icon: Users },
    { to: '/events', label: 'Events', icon: Calendar },
    { to: `/clubs/${clubSlug}`, label: 'Back to Club', icon: Users },
    { to: '/about', label: 'About', icon: Info },
    { to: '/faq', label: 'FAQ', icon: HelpCircle },
    { to: '/terms', label: 'Terms', icon: FileText },
    { to: '/privacy', label: 'Privacy', icon: Shield }
  ],
  contextActions: [
    { 
      label: 'Create Event', 
      onClick: () => navigate(`/clubs/${clubSlug}/events/create`), 
      icon: Plus,
      show: isLeader // Leader-only action - from useClub(), never calculated locally
    }
  ],
  authentication: [
    { label: 'Sign Out', onClick: logout, variant: 'destructive', icon: LogOut }
  ]
});

/**
 * Club Chat Page Menu
 * Simple navigation - no context actions for chat
 */
export const createClubChatMobileMenu = (
  _navigate: ReturnType<typeof useNavigate>,
  logout: () => void,
  clubSlug: string
): MobileMenuConfig => ({
  navigation: [
    { to: '/home', label: 'Home', icon: Home },
    { to: '/clubs', label: 'Clubs', icon: Users },
    { to: '/events', label: 'Events', icon: Calendar },
    { to: `/clubs/${clubSlug}`, label: 'Back to Club', icon: Users },
    { to: '/about', label: 'About', icon: Info },
    { to: '/faq', label: 'FAQ', icon: HelpCircle },
    { to: '/terms', label: 'Terms', icon: FileText },
    { to: '/privacy', label: 'Privacy', icon: Shield }
  ],
  contextActions: [],
  authentication: [
    { label: 'Sign Out', onClick: logout, variant: 'destructive', icon: LogOut }
  ]
});

/**
 * Member Detail Page Menu
 * Simple navigation for viewing other member profiles
 */
export const createMemberDetailMobileMenu = (
  _navigate: ReturnType<typeof useNavigate>,
  logout: () => void
): MobileMenuConfig => ({
  navigation: [
    { to: '/home', label: 'Home', icon: Home },
    { to: '/clubs', label: 'Clubs', icon: Users },
    { to: '/events', label: 'Events', icon: Calendar },
    { to: '/me', label: 'My Profile', icon: User },
    { to: '/about', label: 'About', icon: Info },
    { to: '/faq', label: 'FAQ', icon: HelpCircle },
    { to: '/terms', label: 'Terms', icon: FileText },
    { to: '/privacy', label: 'Privacy', icon: Shield }
  ],
  contextActions: [],
  authentication: [
    { label: 'Sign Out', onClick: logout, variant: 'destructive', icon: LogOut }
  ]
});

/**
 * Public Profile Page Menu
 * Simple navigation for public profile views
 */
export const createPublicProfileMobileMenu = (
  _navigate: ReturnType<typeof useNavigate>,
  logout: () => void
): MobileMenuConfig => ({
  navigation: [
    { to: '/home', label: 'Home', icon: Home },
    { to: '/clubs', label: 'Clubs', icon: Users },
    { to: '/events', label: 'Events', icon: Calendar },
    { to: '/me', label: 'My Profile', icon: User },
    { to: '/about', label: 'About', icon: Info },
    { to: '/faq', label: 'FAQ', icon: HelpCircle },
    { to: '/terms', label: 'Terms', icon: FileText },
    { to: '/privacy', label: 'Privacy', icon: Shield }
  ],
  contextActions: [],
  authentication: [
    { label: 'Sign Out', onClick: logout, variant: 'destructive', icon: LogOut }
  ]
});

/**
 * Platform Admin Home Page Menu
 * Admin-specific navigation and actions
 */
export const createPlatformAdminHomeMobileMenu = (
  navigate: ReturnType<typeof useNavigate>,
  logout: () => void
): MobileMenuConfig => ({
  navigation: [
    { to: '/home', label: 'Home', icon: Home },
    { to: '/clubs', label: 'Clubs', icon: Users },
    { to: '/events', label: 'Events', icon: Calendar },
    { to: '/about', label: 'About', icon: Info },
    { to: '/faq', label: 'FAQ', icon: HelpCircle },
    { to: '/terms', label: 'Terms', icon: FileText },
    { to: '/privacy', label: 'Privacy', icon: Shield }
  ],
  contextActions: [
    { 
      label: 'Manage Clubs', 
      onClick: () => navigate('/platform-admin/clubs'), 
      icon: Settings
    },
    { 
      label: 'Manage Invites', 
      onClick: () => navigate('/admin/invites'), 
      icon: UserPlus
    }
  ],
  authentication: [
    { label: 'Sign Out', onClick: logout, variant: 'destructive', icon: LogOut }
  ]
});

/**
 * Platform Admin Clubs Page Menu
 * Admin club management navigation
 */
export const createPlatformAdminClubsMobileMenu = (
  navigate: ReturnType<typeof useNavigate>,
  logout: () => void
): MobileMenuConfig => ({
  navigation: [
    { to: '/home', label: 'Home', icon: Home },
    { to: '/clubs', label: 'Clubs', icon: Users },
    { to: '/events', label: 'Events', icon: Calendar },
    { to: '/platform-admin/home', label: 'Admin Home', icon: Settings },
    { to: '/about', label: 'About', icon: Info },
    { to: '/faq', label: 'FAQ', icon: HelpCircle },
    { to: '/terms', label: 'Terms', icon: FileText },
    { to: '/privacy', label: 'Privacy', icon: Shield }
  ],
  contextActions: [
    { 
      label: 'Create Club', 
      onClick: () => navigate('/clubs/create'), 
      icon: Plus
    }
  ],
  authentication: [
    { label: 'Sign Out', onClick: logout, variant: 'destructive', icon: LogOut }
  ]
});

/**
 * Admin Invites Page Menu
 * Admin invite management with create action
 */
export const createAdminInvitesMobileMenu = (
  _navigate: ReturnType<typeof useNavigate>,
  logout: () => void,
  setShowCreateModal: (show: boolean) => void
): MobileMenuConfig => ({
  navigation: [
    { to: '/home', label: 'Home', icon: Home },
    { to: '/clubs', label: 'Clubs', icon: Users },
    { to: '/events', label: 'Events', icon: Calendar },
    { to: '/platform-admin/home', label: 'Admin Home', icon: Settings },
    { to: '/about', label: 'About', icon: Info },
    { to: '/faq', label: 'FAQ', icon: HelpCircle },
    { to: '/terms', label: 'Terms', icon: FileText },
    { to: '/privacy', label: 'Privacy', icon: Shield }
  ],
  contextActions: [
    { 
      label: 'Create Invite', 
      onClick: () => setShowCreateModal(true), 
      icon: Plus
    }
  ],
  authentication: [
    { label: 'Sign Out', onClick: logout, variant: 'destructive', icon: LogOut }
  ]
});

// ===============================
// MAIN COMPONENT
// ===============================

function MobileContextMenu({ config, isOpen, onClose, className = '' }: MobileContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  
  // Filter items based on show property
  const visibleNavigation = config.navigation?.filter(item => item.show !== false) || [];
  const visibleContextActions = config.contextActions?.filter(action => action.show !== false) || [];
  const visibleAuthActions = config.authentication?.filter(action => action.show !== false) || [];

  // ===============================
  // EVENT HANDLERS
  // ===============================

  // Close menu on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  // Close menu on escape key
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  // Focus management - trap focus within menu
  useEffect(() => {
    if (isOpen && menuRef.current) {
      const focusableElements = menuRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      
      if (focusableElements.length > 0) {
        (focusableElements[0] as HTMLElement).focus();
      }
    }
  }, [isOpen]);

  const handleItemClick = (onClick?: () => void) => {
    if (onClick) {
      onClick();
    }
    onClose();
  };

  // ===============================
  // RENDER HELPERS
  // ===============================

  const renderNavigationSection = () => {
    if (visibleNavigation.length === 0) return null;

    return (
      <div>
        <nav role="menubar" aria-label="Main navigation">
          {visibleNavigation.map((item, index) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => handleItemClick()}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 hover:bg-oro-surface-alt transition-colors first:rounded-t-lg ${
                  isActive ? 'text-oro-gold bg-oro-surface-alt' : 'text-text-primary'
                }`
              }
              role="menuitem"
              style={{
                animationDelay: `${index * 50}ms` // Staggered entrance
              }}
            >
              <item.icon className="w-4 h-4" />
              <span className="font-medium text-sm">{item.label}</span>
            </NavLink>
          ))}
        </nav>
        {(visibleContextActions.length > 0 || visibleAuthActions.length > 0) && (
          <div className="border-t border-oro-border my-1" />
        )}
      </div>
    );
  };

  const renderContextActionsSection = () => {
    if (visibleContextActions.length === 0) return null;

    return (
      <div role="group" aria-label="Page actions">
        {visibleContextActions.map((action, index) => (
          <button
            key={action.label}
            onClick={() => handleItemClick(action.onClick)}
            className={`flex items-center gap-3 px-4 py-3 w-full text-left hover:bg-oro-surface-alt transition-colors ${
              action.variant === 'destructive' 
                ? 'text-red-600 hover:text-red-700' 
                : 'text-text-primary'
            }`}
            role="menuitem"
            style={{
              animationDelay: `${(visibleNavigation.length + index) * 50}ms`
            }}
          >
            <action.icon className="w-4 h-4" />
            <span className="font-medium text-sm">{action.label}</span>
          </button>
        ))}
        {visibleAuthActions.length > 0 && (
          <div className="border-t border-oro-border my-1" />
        )}
      </div>
    );
  };

  const renderAuthenticationSection = () => {
    if (visibleAuthActions.length === 0) return null;

    return (
      <div role="group" aria-label="Account">
        {visibleAuthActions.map((action, index) => (
          <button
            key={action.label}
            onClick={() => handleItemClick(action.onClick)}
            className={`flex items-center gap-3 px-4 py-3 w-full text-left hover:bg-oro-surface-alt transition-colors last:rounded-b-lg ${
              action.variant === 'destructive' 
                ? 'text-red-600 hover:text-red-700' 
                : 'text-text-primary'
            }`}
            role="menuitem"
            style={{
              animationDelay: `${(visibleNavigation.length + visibleContextActions.length + index) * 50}ms`
            }}
          >
            {action.icon && <action.icon className="w-4 h-4" />}
            {!action.icon && <LogOut className="w-4 h-4" />}
            <span className="font-medium text-sm">{action.label}</span>
          </button>
        ))}
      </div>
    );
  };

  // ===============================
  // RENDER
  // ===============================

  if (!isOpen) return null;

  return (
    <>
      {/* Compact Dropdown Menu - No full-screen backdrop needed */}
      <div
        ref={menuRef}
        className={`
          absolute top-full right-0 z-50 mt-1
          bg-oro-surface/95 backdrop-blur-sm border border-oro-border shadow-xl rounded-lg
          animate-in fade-in slide-in-from-top-2 duration-200
          md:hidden
          min-w-56 max-w-80 w-max
          ${className}
        `}
        role="menu"
        aria-hidden={!isOpen}
        aria-label="Mobile navigation menu"
      >
        {/* Menu Content - No header needed for compact design */}
        <div className="py-2">
          {renderNavigationSection()}
          {renderContextActionsSection()}
          {renderAuthenticationSection()}
        </div>
      </div>
    </>
  );
}

// ===============================
// EXPORTS
// ===============================

export default MobileContextMenu;

// ===============================
// USAGE EXAMPLES
// ===============================

/*
// Example usage in ProfilePage:

import MobileContextMenu, { createProfileMobileMenu } from '../components/MobileContextMenu';

function ProfilePage() {
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const profileMenuConfig = createProfileMobileMenu(navigate, logout);

  return (
    <div>
      <TopBar 
        title="Profile"
        mobileMenu={profileMenuConfig} // New prop
        // onMenu={() => { ... }} // Legacy prop - still supported
      />
      
      <MobileContextMenu
        config={profileMenuConfig}
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
      />
    </div>
  );
}
*/